import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Clapperboard,
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import {
  Card,
  Field,
  Input,
  Textarea,
  Select,
  Toggle,
  ToggleRow,
  Pill,
  TierChip,
  Badge,
  Button,
  Empty,
  TabBar,
  Toaster,
  useToast as useXToast,
  Range,
  ProposalPills,
  XTabBar,
} from "@/components/ui";
import { ProposalPreview } from "@/components/admin/preview/ProposalPreview";
import { BackgroundMusicField } from "./BackgroundMusicField";
import { ImageOrDefaultUploader } from "@/components/admin/uploads/ImageOrDefaultUploader";
import {
  AMBIENT_EFFECTS,
  getAmbientEffectsForSlide,
  type SlideType,
} from "@/components/admin/preview/effects-catalogue";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";
import {
  SLIDE_LIMITS,
  type AnchorType,
  type ProposalTier,
  isProtectedIndex,
  protectedSlideIndices,
  validateDeckStructure,
} from "@/lib/proposalStructure";

type Tier = "FREE" | "PRO";
type Category =
  | "romantic" | "playful" | "adventure" | "elegant"
  | "whimsical" | "serene" | "heartfelt" | "cinematic";

const CATEGORIES: Category[] = [
  "romantic", "playful", "adventure", "elegant",
  "whimsical", "serene", "heartfelt", "cinematic",
];

const CATEGORY_META: Record<Category, { emoji: string; label: string }> = {
  romantic: { emoji: "💕", label: "Romantic" },
  playful: { emoji: "🎈", label: "Playful" },
  adventure: { emoji: "🗺️", label: "Adventure" },
  elegant: { emoji: "🥂", label: "Elegant" },
  whimsical: { emoji: "✨", label: "Whimsical" },
  serene: { emoji: "🌙", label: "Serene" },
  heartfelt: { emoji: "💌", label: "Heartfelt" },
  cinematic: { emoji: "🎬", label: "Cinematic" },
};

const SLIDE_TYPES: Array<{ value: string; label: string; icon: string; hint: string; needsText: boolean }> = [
  { value: "TITLE",    label: "Title",     icon: "T",  hint: "Bold headline that anchors the moment",       needsText: true },
  { value: "MESSAGE",  label: "Message",   icon: "M",  hint: "Personal note in your voice",                 needsText: true },
  { value: "PHOTO",    label: "Photo",     icon: "P",  hint: "Hero image with Ken Burns motion",            needsText: true },
  { value: "MEMORY",   label: "Memory",    icon: "M",  hint: "Polaroid-style keepsake",                     needsText: true },
  { value: "QUESTION", label: "Question",  icon: "?",  hint: "The big ask, letter by letter",              needsText: true },
  { value: "FINAL",    label: "Final",     icon: "💕", hint: "Climax with confetti burst",                 needsText: true },
];

const PROPOSAL_TYPES = [
  { value: "MARRIAGE",    label: "💍 Marriage" },
  { value: "RELATIONSHIP", label: "💕 Relationship" },
  { value: "DATE", label: "🍹 Date" },
];

const TIERS: Array<{ value: Tier; label: string; tagline: string }> = [
  { value: "FREE", label: "Free", tagline: "Visible to everyone — limited to free effects; no credit held" },
  { value: "PRO",  label: "Pro",  tagline: "Costs 1 credit to pick — unlocks the full effect library" },
];

const LOCALES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
  { code: "ko", label: "한국어" },
];

interface Slide {
  order: number;
  type: string;
  contentText: string;
  imageUrl: string;
  effectEntry: string;
  effectAmbient: string;
  effectIntensity: number;
  duration: number;
  enableAutoAdvance: boolean;
  /** Per-slide page transition override. `null` = use the deck-level default. */
  pageTransitionId: string | null;
}

interface Translation {
  locale: string;
  name: string;
  description: string;
}

interface FormState {
  slug: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  category: Category | "";
  tier: Tier;
  isPublic: boolean;
  isFeatured: boolean;
  proposalTypes: string[];
  /** Top-level opening animation played before the cinematic intro.
   *  Defaults to 'theatre-curtain' for new templates. */
  openingAnimation: string;
  /** Top-level default background layered behind every slide.
   *  Defaults to 'ambient-orbs' (the drifting colour orbs ported from
   *  why-merryfix_implementation). */
  defaultBackground: string;
  /** Page transition (slide → slide) code from ProposalTransition.
   *  `null` = use the built-in iris-cut default. The picker in the
   *  preview modal writes here so admins can audition each transition
   *  inline and persist the choice via the template PUT. */
  pageTransitionId: string | null;
  /** Background-music track URL (in-built /audio/*.mp3 or an
   *  AI-generated hosted URL). `null` = no music. Stored on the
   *  proposal level and surfaced in the proposal template seed. */
  musicUrl: string | null;
  /** Optional label for the chosen music track. */
  musicType: string | null;
  /** Mute toggle for the in-modal preview surface. Defaults to off. */
  previewMuted: boolean;
  slides: Slide[];
  translations: Translation[];
}

const SLUG_TIER: Record<Tier, "FREE" | "PRO"> = {
  FREE: "FREE",
  PRO: "PRO",
};

const OPENING_ANIMATIONS = [
  { value: "cinematic-curtain", label: "🎭 Cinematic Curtain" },
  { value: "none", label: "∅ None" },
];

const DEFAULT_BACKGROUNDS = AMBIENT_EFFECTS.filter(e => e.code !== 'none').map((e) => ({
  value: e.code,
  label: e.tier === 'PRO' ? `${e.label} · PRO` : e.label,
}));

const emptyForm: FormState = {
  slug: "",
  name: "",
  description: "",
  thumbnailUrl: "",
  category: "",
  tier: "PRO",
  isPublic: true,
  isFeatured: false,
  proposalTypes: ["MARRIAGE"],
  openingAnimation: "cinematic-curtain",
  defaultBackground: "floating-heart-bokeh",
  pageTransitionId: null,
  musicUrl: null,
  musicType: null,
  previewMuted: false,
  slides: [
    { order: 0, type: "TITLE",    contentText: "", imageUrl: "", effectEntry: "fade", effectAmbient: "floating-heart-bokeh", effectIntensity: 0.5, duration: 5, enableAutoAdvance: true, pageTransitionId: null },
    { order: 1, type: "MESSAGE",  contentText: "", imageUrl: "", effectEntry: "fade", effectAmbient: "velvet-dust-motes", effectIntensity: 0.4, duration: 8, enableAutoAdvance: true, pageTransitionId: null },
    { order: 2, type: "QUESTION", contentText: "", imageUrl: "", effectEntry: "fade", effectAmbient: "champagne-bubble-rise", effectIntensity: 0.7, duration: 10, enableAutoAdvance: true, pageTransitionId: null } as any,
    { order: 3, type: "FINAL",    contentText: "", imageUrl: "", effectEntry: "fade", effectAmbient: "entwined-particle-vines", effectIntensity: 1.0, duration: 30, enableAutoAdvance: true, pageTransitionId: null },
  ],
  translations: [],
};

const FREE_AMBIENT_COUNT = AMBIENT_EFFECTS.filter((e) => e.tier === "FREE" && e.code !== 'none').length;

function slideTypeMeta(type: string) {
  return SLIDE_TYPES.find((t) => t.value === type) ?? SLIDE_TYPES[0];
}

function clampDuration(n: number) {
  if (Number.isNaN(n)) return 5;
  return Math.min(120, Math.max(1, Math.round(n)));
}

function ambientOptionsFor(slideType: SlideType) {
  return getAmbientEffectsForSlide(slideType).map((e) => ({
    value: e.code,
    label: e.tier === "PRO" ? `${e.label} · PRO` : e.label,
  }));
}
function recommendedAmbientFor(slideType: SlideType) {
  return getAmbientEffectsForSlide(slideType).find((e) => e.recommended === slideType);
}

const isProAmbient = (code: string) => AMBIENT_EFFECTS.find((e) => e.code === code)?.tier === "PRO";

/* ──────────────────────────────────────────────────────────────── */
// Page transitions: tiny client-side hook + selector. We hit the
// admin endpoint so disabled rows still appear in the dropdown,
// matching what ProposalPreview does.
// ────────────────────────────────────────────────────────────────

interface PageTransitionRow {
  id: string;
  name: string;
  emoji: string | null;
  tier: 'FREE' | 'PRO';
  isEnabled: boolean;
  cssDataT: string | null;
  kind: 'fm' | 'css';
}

function usePageTransitions(): { rows: PageTransitionRow[]; loading: boolean; error: string | null } {
  const [rows, setRows] = useState<PageTransitionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .get<{ success: boolean; transitions: PageTransitionRow[] }>('/admin/transitions')
      .then((res) => {
        if (cancelled) return;
        if (res.success) setRows(res.transitions);
        setLoading(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(
          e?.status
            ? `Failed to load transitions (${e.status}). Did you run \`npm run seed:transitions\`?`
            : e?.message ?? 'Failed to load transitions',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { rows, loading, error };
}

/* ──────────────────────────────────────────────────────────────── */

export default function TemplateEditorPage() {
  const params = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const isNew = params.id === "new";
  const kind = search.get("kind") || "basic";

  const [form, setForm] = useState<FormState | null>(null);
  const [snapshot, setSnapshot] = useState<string>("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<"details" | "slides" | "translations" | "settings">("details");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [preview, setPreview] = useState<{ open: boolean; mode: "deck" | "showcase" }>({ open: false, mode: "deck" });
  const [saving, setSaving] = useState(false);
  const slugTouchedRef = useRef(false);
  const { showToast } = useXToast();

  /* ── load ──────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isNew) {
        try {
          const res = await adminApi.get<{ success: boolean; template: any }>(
            `/admin/templates/${kind}/${params.id}`,
          );
          if (res.success) {
            const t = res.template;
            const loaded: FormState = {
              slug: t.slug ?? "",
              name: t.name ?? "",
              description: t.description ?? "",
              thumbnailUrl: t.thumbnailUrl ?? "",
              category: (t.category ?? "") as Category | "",
              tier: (t.tier ?? "PRO") as Tier,
              isPublic: !!t.isPublic,
              isFeatured: !!t.isFeatured,
              proposalTypes:
                t.proposalTypes && t.proposalTypes.length
                  ? t.proposalTypes
                  : ["MARRIAGE"],
              openingAnimation: t.openingAnimation ?? "cinematic-curtain",
              defaultBackground: t.defaultBackground ?? "floating-heart-bokeh",
              pageTransitionId: (t as any).pageTransitionId ?? null,
              musicUrl: t.musicUrl ?? null,
              musicType: t.musicType ?? null,
              previewMuted: false,
              slides: (t.slides ?? []).sort((a: any, b: any) => a.order - b.order).map((s: any) => ({
                order: s.order ?? 0,
                type: s.type ?? "MESSAGE",
                contentText: s.contentText ?? "",
                imageUrl: s.imageUrl ?? "",
                effectEntry: s.effectEntry ?? "fade",
                effectAmbient: s.effectAmbient ?? "none",
                effectIntensity: s.effectIntensity ?? 0.5,
                duration: s.duration ?? 5,
                enableAutoAdvance: s.enableAutoAdvance ?? true,
                pageTransitionId: s.pageTransitionId ?? null,
              })),
              translations: (t.translations ?? []).map((x: any) => ({
                locale: x.locale,
                name: x.name ?? "",
                description: x.description ?? "",
              })),
            };
            if (!cancelled) {
              setForm(loaded);
              setSnapshot(JSON.stringify(loaded));
              slugTouchedRef.current = true;
            }
            return;
          }
        } catch (e: any) {
          if (!cancelled) setLoadError(e?.message || "Failed to load template");
          return;
        }
      }
      // New template — start from blank.
      if (!cancelled) {
        setForm({ ...emptyForm });
        setSnapshot(JSON.stringify(emptyForm));
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [params.id, isNew, kind]);

  /* ── update helpers ─────────────────────────────── */

  const patch = useCallback((p: Partial<FormState>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...p };
      // Auto-derive the slug from the name until the admin edits it by hand.
      if (!isNew && !slugTouchedRef.current && p.name !== undefined) {
        next.slug = p.name.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-|-$/g, "");
      }
      return next;
    });
  }, [isNew]);

  const handleSlugEdit = (slug: string) => {
    slugTouchedRef.current = true;
    setForm((prev) => (prev ? { ...prev, slug } : prev));
  };

  const updateSlide = (idx: number, p: Partial<Slide>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const slides = prev.slides.map((s, i) => (i === idx ? { ...s, ...p } : s));
      return { ...prev, slides };
    });
  };

  const addSlide = () => {
    setForm((prev) => {
      if (!prev) return prev;
      const tier: ProposalTier = prev.tier === "FREE" ? "FREE" : "PRO";
      const limit = SLIDE_LIMITS[tier];
      if (prev.slides.length >= limit) {
        showToast({
          title: `Slide cap reached`,
          description: `${tier} templates cap at ${limit} slides.`,
          variant: "error",
        });
        return prev;
      }
      const newSlide: Slide = {
        order: prev.slides.length,
        type: "MESSAGE",
        contentText: "",
        imageUrl: "",
        effectEntry: "fade",
        effectAmbient: "none",
        effectIntensity: 0.5,
        duration: 5,
        enableAutoAdvance: true,
        pageTransitionId: null,
      };
      showToast({ title: "Message slide added at the end of the deck." });
      setExpanded((e) => ({ ...e, [newSlide.order]: true }));
      setTab("slides");
      return { ...prev, slides: [...prev.slides, newSlide] };
    });
  };

  const removeSlide = (idx: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      // Once the deck reaches the minimum-slide rule, any deletion is
      // locked — both the three anchor positions AND the
      // minimum-count guard. Below the minimum the deck is in "repair"
      // mode and any slide can go.
      if (prev.slides.length >= 5 && isProtectedIndex(prev.slides.length, idx)) {
        showToast({
          title: `Slide ${idx + 1} is locked`,
          description:
            "The opening, The Ask and the Celebration slides are anchor positions. They can't be deleted until you replace them with another slide.",
          variant: "error",
        });
        return prev;
      }
      if (prev.slides.length <= 5) {
        showToast({
          title: 'Deck is at its minimum',
          description: 'Add another slide before deleting one.',
          variant: 'error',
        });
        return prev;
      }
      showToast({ title: 'Slide removed.' });
      return {
        ...prev,
        slides: prev.slides.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })),
      };
    });
  };

  const reorderSlide = (idx: number, dir: -1 | 1) => {
    setForm((prev) => {
      if (!prev) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.slides.length) return prev;
      // Same anchor protection for swaps: the three positional anchors
      // are pinned. Allow no-op moves (idx === target).
      if (
        prev.slides.length >= 5 &&
        (isProtectedIndex(prev.slides.length, idx) ||
          isProtectedIndex(prev.slides.length, target))
      ) {
        showToast({
          title: "Anchor slides are locked",
          description:
            "Move them by editing slide contents instead. The opening, The Ask and the Celebration live at the front, position N-2 and the end of every deck.",
          variant: "error",
        });
        return prev;
      }
      const slides = [...prev.slides];
      [slides[idx], slides[target]] = [slides[target], slides[idx]];
      return { ...prev, slides: slides.map((s, i) => ({ ...s, order: i })) };
    });
  };

  const toggleExpand = (idx: number) => {
    setExpanded((e) => ({ ...e, [idx]: !e[idx] }));
  };

  const updateTranslation = (locale: string, p: Partial<Translation>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const exists = prev.translations.find((t) => t.locale === locale);
      const translations = exists
        ? prev.translations.map((t) => (t.locale === locale ? { ...t, ...p } : t))
        : [...prev.translations, { locale, name: "", description: "", ...p }];
      return { ...prev, translations };
    });
  };

  const removeTranslation = (locale: string) => {
    setForm((prev) => prev ? { ...prev, translations: prev.translations.filter((t) => t.locale !== locale) } : prev);
  };

  /* ── save ─────────────────────────────────────────── */

  const dirty = useMemo(() => !!form && JSON.stringify(form) !== snapshot, [form, snapshot]);

  // Save is blocked when the deck fails structural validation. The
  // backend will also reject these saves with a 400 + reason code, but
  // disabling the button here gives admins a faster feedback loop
  // and keeps the warning banner consistent.
  const structureGate = useMemo(() => {
    if (!form) return { ok: true as const };
    const tier: ProposalTier = form.tier === "FREE" ? "FREE" : "PRO";
    return validateDeckStructure(form.slides, tier);
  }, [form]);
  const saveBlockedByStructure = !structureGate.ok;

  /* Preview slides — memoized so the array reference (and the JSON
     content-signature downstream) only changes when something the
     preview renders actually changes. Without this the preview's
     `useEffect([slides])` (and the pageTransition content-signature
     effect) re-fire on every editor keystroke, throwing the player
     back to slide 1 mid-preview. */
  const previewSlides = useMemo(
    () =>
      (form?.slides ?? []).map((s) => ({
        type: s.type,
        contentText: s.contentText || "",
        imageUrl: s.imageUrl || "",
        effectEntry: s.effectEntry,
        effectAmbient: s.effectAmbient,
        effectIntensity: s.effectIntensity,
        duration: s.duration,
        enableAutoAdvance: s.enableAutoAdvance,
        pageTransitionId: s.pageTransitionId,
      })),
    [form?.slides],
  );

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    const payload = {
      name: form.name.trim() || "Untitled template",
      slug: (form.slug || form.name).toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-|-$/g, "") || "untitled-template",
      description: form.description.slice(0, 240),
      category: form.category,
      tier: form.tier,
      proposalTypes: form.proposalTypes.length ? form.proposalTypes : ["MARRIAGE"],
      thumbnailUrl: form.thumbnailUrl,
      isPublic: form.isPublic,
      isFeatured: form.isFeatured,
      openingAnimation: form.openingAnimation,
      defaultBackground: form.defaultBackground,
      pageTransitionId: form.pageTransitionId,
      musicUrl: form.musicUrl,
      musicType: form.musicType,
      slides: form.slides.map((s, index) => ({
        order: typeof s.order === "number" ? s.order : index,
        type: s.type,
        contentText: s.contentText.slice(0, 5000),
        // The legacy editor serialised blank fields as the empty
        // string. Backend validation treats "" as a malformed URL
        // and rejects the whole template save. Normalise empty
        // inputs to null client-side so we don't bother the server
        // with obvious blanks.
        imageUrl: s.imageUrl?.trim() ? s.imageUrl : null,
        effectEntry: s.effectEntry,
        effectAmbient: s.effectAmbient,
        effectIntensity: s.effectIntensity,
        duration: clampDuration(s.duration),
        enableAutoAdvance: s.enableAutoAdvance,
        pageTransitionId: s.pageTransitionId?.trim() ? s.pageTransitionId : null,
      })),
      translations: form.translations
        .filter((t) => t.name.trim() || t.description.trim())
        .map((t) => ({ locale: t.locale, name: t.name, description: t.description })),
    };
    try {
      if (isNew) {
        await adminApi.post("/admin/templates/basic", payload);
        showToast({ title: "Template created.", variant: "success" });
      } else {
        await adminApi.patch(`/admin/templates/basic/${params.id}`, payload);
        showToast({ title: "Template saved.", variant: "success" });
      }
      setSnapshot(JSON.stringify(form));
      setTimeout(() => navigate("/templates"), 600);
    } catch (e: any) {
      showToast({ title: e?.message || "Save failed", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* ── loading / error states ─────────────────────── */

  if (loadError) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-24 text-center">
        <p className="font-display text-2xl italic text-cream">{loadError}</p>
        <Button variant="primary" className="mt-6" onClick={() => navigate("/templates")}>
          <ArrowLeft size={14} /> Back to library
        </Button>
      </main>
    );
  }

  if (!form) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <div className="flex items-center gap-3 text-dim">
          <Loader2 size={18} className="animate-spin text-rose" />
          <span className="text-[13px] font-semibold tracking-wide">Loading template…</span>
        </div>
      </main>
    );
  }

  const displayName = form.name.trim() || "Untitled template";
  const activeTranslations = form.translations.filter((t) => t.name.trim() || t.description.trim());
  const isPro = form.tier === "PRO";

  const tabs: Array<{ id: "details" | "slides" | "translations" | "settings"; label: string }> = [
    { id: "details", label: "Details" },
    { id: "slides", label: `Slides (${form.slides.length})` },
    { id: "translations", label: `Translations (${activeTranslations.length})` },
    { id: "settings", label: "Settings" },
  ];

  return (
    <Toaster>
      {/* ── Sticky top bar ────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-line bg-night/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/templates")}>
            <ArrowLeft size={14} /> Back
          </Button>

          <div className="hidden items-center gap-2 sm:flex">
            {dirty ? (
              <>
                <span className="dirty-dot" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gold/90">
                  Unsaved changes
                </span>
              </>
            ) : (
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-dim">
                All changes saved
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:flex"
              onClick={() => setPreview({ open: true, mode: "showcase" })}
            >
              <Wand2 size={14} className="text-gold" /> Effect showcase
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPreview({ open: true, mode: "deck" })}>
              <Eye size={14} /> Preview
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={saving || !dirty || saveBlockedByStructure}
              title={
                saveBlockedByStructure
                  ? "Fix the structural warning above before saving."
                  : undefined
              }
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? "Create template" : "Save changes"}
            </Button>
          </div>
        </div>
      </header>

      {/* ── Header ────────────────────────────────── */}
      <div className="mx-auto max-w-6xl px-4 pb-6 pt-8 sm:px-6">
        <div className="reveal flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-rose/85">
              MerryFlix Studio · Template editor
            </p>
            <h1 className="font-display text-[30px] font-semibold leading-tight text-cream sm:text-[34px]">
              {isNew ? (
                <em>New template</em>
              ) : (
                <>
                  Edit template — <em className="text-blush">{displayName}</em>
                </>
              )}
            </h1>
            <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-muted">
              {form.description.trim() ||
                "Craft the deck that asks the question — slides, cinematic effects, translations."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <TierChip tier={SLUG_TIER[form.tier]} />
            {form.category && (
              <Pill tone="cream">
                {CATEGORY_META[form.category].emoji} {CATEGORY_META[form.category].label}
              </Pill>
            )}
            {form.isPublic ? <Pill tone="mint">Public</Pill> : <Pill tone="muted">Draft</Pill>}
            {form.isFeatured && <Pill tone="gold">★ Featured</Pill>}
          </div>
        </div>

        <XTabBar
          value={tab}
          options={tabs}
          onChange={setTab}
        />
      </div>

      {/* ── Body ──────────────────────────────────── */}
      <main className="mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <div key={tab} className="reveal">
          {tab === "details" && (
            <DetailsTab
              form={form}
              onPatch={patch}
              onSlugEdit={handleSlugEdit}
            />
          )}
          {tab === "slides" && (
            <SlidesTab
              form={form}
              expanded={expanded}
              onToggle={toggleExpand}
              onUpdate={updateSlide}
              onAdd={addSlide}
              onRemove={removeSlide}
              onReorder={reorderSlide}
              patch={patch}
              isPro={isPro}
            />
          )}
          {tab === "translations" && (
            <TranslationsTab
              translations={form.translations}
              onUpdate={updateTranslation}
              onRemove={removeTranslation}
            />
          )}
          {tab === "settings" && (
            <SettingsTab form={form} onPatch={patch} />
          )}
        </div>
      </main>

      {/* ── Preview modal ────────────────────────── */}
      {/* Slides are memoized into a stable `PreviewSlideData[]` so the
          preview's content-signature effect (which uses JSON.stringify)
          only fires when something the preview actually cares about
          changes — not on every render of the editor's form state. */}
      <ProposalPreview
        open={preview.open}
        onClose={() => setPreview((p) => ({ ...p, open: false }))}
        title={form.name || "Template preview"}
        slides={previewSlides}
        proposalType={(form.proposalTypes[0] as any) || "MARRIAGE"}
        openingAnimation={form.openingAnimation as any}
        defaultBackground={form.defaultBackground as any}
        pageTransitionId={form.pageTransitionId}
        onPageTransitionChange={(id) => patch({ pageTransitionId: id })}
        musicUrl={form.musicUrl}
        isMuted={form.previewMuted}
      />

      {/* ── Mobile effect-showcase FAB ───────────── */}
      <button
        type="button"
        onClick={() => setPreview({ open: true, mode: "showcase" })}
        className="btn-x-ghost cursor-pointer fixed bottom-5 left-5 z-50 sm:hidden"
        aria-label="Open effect showcase"
      >
        <Wand2 size={15} className="text-gold" />
      </button>
    </Toaster>
  );
}

/* ── Tabs ────────────────────────────────────────── */

function DetailsTab({
  form,
  onPatch,
  onSlugEdit,
}: {
  form: FormState;
  onPatch: (p: Partial<FormState>) => void;
  onSlugEdit: (slug: string) => void;
}) {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        <Field label="Template name" required hint="Shown in the library. Empty names render as &ldquo;Untitled template&rdquo;.">
          <input
            className="inp"
            placeholder="e.g. Midnight in Paris"
            value={form.name}
            maxLength={120}
            onChange={(e) => onPatch({ name: e.target.value })}
          />
        </Field>

        <Field label="URL slug" required hint="Auto-normalized — lowercase letters, numbers and dashes only.">
          <div className="flex items-center gap-2">
            <span className="hidden select-none text-[12.5px] text-dim sm:block">merryflix.app/t/</span>
            <input
              className="inp font-mono text-[12.5px]"
              value={form.slug}
              onChange={(e) => onSlugEdit(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
            />
          </div>
        </Field>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Category" hint="Sets the mood bucket in the public library.">
            <select
              className="inp cursor-pointer"
              value={form.category}
              onChange={(e) => onPatch({ category: e.target.value as Category | "" })}
            >
              <option value="">Uncategorized</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_META[c].emoji} {CATEGORY_META[c].label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Subscription tier" required hint="Gates who can use it — and which effects are allowed.">
            <select
              className="inp cursor-pointer"
              value={form.tier}
              onChange={(e) => onPatch({ tier: e.target.value as Tier })}
            >
              {TIERS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label} — {t.tagline}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field
          label="Description"
          counter={`${form.description.length} / 240`}
          hint="Shown as the sub-headline in the public library."
        >
          <textarea
            rows={3}
            className="inp"
            maxLength={240}
            placeholder="One line that sells the mood…"
            value={form.description}
            onChange={(e) => onPatch({ description: e.target.value.slice(0, 240) })}
          />
        </Field>

        <Field label="Proposal types" required hint="MARRIAGE for legacy, RELATIONSHIP for the &ldquo;ask&rdquo; surface, DATE for the lighter flow.">
          <ProposalPills
            value={form.proposalTypes}
            onChange={(v) => onPatch({ proposalTypes: v })}
            options={PROPOSAL_TYPES}
          />
        </Field>

        <div className="card px-4 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="lbl">Opening &amp; Background</p>
              <p className="mt-1 text-[11.5px] leading-snug text-dim">
                Plays once when the proposee opens the proposal. Admins can override the
                defaults per template.
              </p>
            </div>
            <Pill tone="rose">Cinematic</Pill>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Opening animation"
              hint="Theatre Curtain is the new default — two velvet panels part before the cinematic intro."
            >
              <select
                className="inp cursor-pointer"
                value={form.openingAnimation}
                onChange={(e) => onPatch({ openingAnimation: e.target.value })}
              >
                {OPENING_ANIMATIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>

            <Field
              label="Default background"
              hint="Ambient Orbs is the new default — three drifting colour orbs ported from the marketing landing."
            >
              <select
                className="inp cursor-pointer"
                value={form.defaultBackground}
                onChange={(e) => onPatch({ defaultBackground: e.target.value })}
              >
                {DEFAULT_BACKGROUNDS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Page transition picker — proposal-level setting. This is the
              same picker exposed in ProposalPreview's header; rendering
              it here lets admins pick + persist the choice without
              having to open the preview modal first. The catalogue is
              fetched from /api/transitions once on mount. */}
          <PageTransitionField
            value={form.pageTransitionId}
            onChange={(id) => onPatch({ pageTransitionId: id })}
          />

          {/* Background music — pulled from the same /audio/platform
              catalogue the public wizard uses. The in-modal ProposalPreview
              uses the URL straight away so the admin can hear the chosen
              track as the deck plays. */}
          <BackgroundMusicField
            url={form.musicUrl}
            type={form.musicType}
            onChange={(url, type) => onPatch({ musicUrl: url, musicType: type })}
          />
        </div>
      </div>

      <div className="space-y-5">
        <Field label="Cover image" hint="Portrait 2:3 — this is the library poster.">
          <ImageOrDefaultUploader
            value={form.thumbnailUrl}
            onChange={(url) => onPatch({ thumbnailUrl: url })}
            kinds={["bg", "fpv_flower", "fpv_ring", "fpv_dinner"]}
          />
        </Field>

        <div className="card divide-y divide-line/70 px-4 py-1">
          <div className="pt-2 pb-1">
            <p className="lbl">Status</p>
          </div>
          <ToggleRow
            title="Public"
            desc="Anyone can browse and use this template"
            checked={form.isPublic}
            onChange={(v) => onPatch({ isPublic: v })}
          />
          <ToggleRow
            title="Featured"
            desc="Showcase this template in the homepage carousel"
            checked={form.isFeatured}
            onChange={(v) => onPatch({ isFeatured: v })}
          />
        </div>
      </div>
    </div>
  );
}

function SlidesTab({
  form,
  expanded,
  onToggle,
  onUpdate,
  onAdd,
  onRemove,
  onReorder,
  patch,
  isPro,
}: {
  form: FormState;
  expanded: Record<number, boolean>;
  onToggle: (idx: number) => void;
  onUpdate: (idx: number, p: Partial<Slide>) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onReorder: (idx: number, dir: -1 | 1) => void;
  /** Patch the top-level form (used by Entry effect to update
   *  `form.pageTransitionId` when the user picks a transition from
   *  the per-slide optgroup). */
  patch: (p: Partial<FormState>) => void;
  isPro: boolean;
}) {
  if (form.slides.length === 0) {
    return (
      <Empty
        icon={<Clapperboard size={24} />}
        title="The deck is empty"
        body="Every proposal deck starts somewhere — add your first slide and set the scene. A classic flow: Title → Messages → Question → Final."
        action={
          <Button variant="primary" onClick={onAdd}>
            <Plus size={15} /> Add first slide
          </Button>
        }
      />
    );
  }

  // Surface structural problems as a banner above the slide list so
  // the admin knows why their next save will be rejected (the same
  // message will also flash as a toast on save).
  const tier: ProposalTier = isPro ? "PRO" : "FREE";
  const structureResult = validateDeckStructure(form.slides, tier);
  const structureBanner = structureResult.ok
    ? null
    : ({
        BAD_FIRST_TYPE: {
          title: "Slide 1 must be a TITLE",
          body: "The opening slide sets the mood. Set Slide 1's type to Title and Save again.",
        },
        BAD_PENULTIMATE_TYPE: {
          title: "Second-to-last slide must be a QUESTION",
          body: "The Ask is where the proposee chooses. Move a Question slide to position N-1 (second to last).",
        },
        BAD_LAST_TYPE: {
          title: "Last slide must be a FINAL",
          body: "The Celebration slide is what the proposee sees after saying YES. Set the last slide's type to Final and Save again.",
        },
        TOO_FEW_SLIDES: {
          title: "Need at least 5 slides",
          body: "Every deck starts with Title, then at least two middle slides (messages, photos, memories), then Question, then Final.",
        },
        TOO_MANY_SLIDES: {
          title: "Too many slides for this tier",
          body: `${tier} templates are capped at ${SLIDE_LIMITS[tier]} slides. Remove a few before saving.`,
        },
      } as const)[structureResult.code];

  return (
    <>
      {structureBanner && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-[13px] text-amber-100">
          <div className="flex items-start gap-3">
            <span className="mt-0.5">⚠</span>
            <div>
              <p className="font-semibold">{structureBanner.title}</p>
              <p className="mt-1 text-amber-100/85">{structureBanner.body}</p>
            </div>
          </div>
        </div>
      )}
    <div className="space-y-3">
      {form.slides.map((slide, i) => (
        <div key={i} className="reveal" style={{ animationDelay: `${Math.min(i, 6) * 45}ms` }}>
          <SlideCard
            slide={slide}
            index={i}
            total={form.slides.length}
            tier={isPro ? "PRO" : "FREE"}
            expanded={!!expanded[i]}
            pageTransitionId={form.pageTransitionId}
            onToggle={() => onToggle(i)}
            onUpdate={(p) => onUpdate(i, p)}
            onMove={(dir) => onReorder(i, dir)}
            onDelete={() => onRemove(i)}
          />
        </div>
      ))}

      <button
        type="button"
        onClick={onAdd}
        className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line py-4 text-[13px] font-semibold text-muted transition-all duration-300 hover:border-rose/50 hover:bg-rose/6 hover:text-blush"
      >
        <Plus size={16} className="transition-transform duration-300 group-hover:rotate-90" />
        Add slide
        <span className="text-[11px] font-normal text-dim">— lands at the end of the deck</span>
      </button>
    </div></>
  );
}

function SlideCard({
  slide,
  index,
  total,
  tier,
  expanded,
  pageTransitionId,
  onToggle,
  onUpdate,
  onMove,
  onDelete,
}: {
  slide: Slide;
  index: number;
  total: number;
  tier: "FREE" | "PRO";
  expanded: boolean;
  /** Current deck-level page-transition id — used as fallback label
   *  when the slide hasn't picked its own override. */
  pageTransitionId: string | null;
  onToggle: () => void;
  onUpdate: (patch: Partial<Slide>) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  const meta = slideTypeMeta(slide.type);
  const slideType = slide.type as SlideType;
  const summary = slide.contentText.trim()
    ? slide.contentText.trim().length > 92
      ? slide.contentText.trim().slice(0, 92) + "…"
      : slide.contentText.trim()
    : meta.hint;
  const recAm = recommendedAmbientFor(slideType);

  // Anchor protection — only kicks in once the deck has reached the
  // minimum slide count. Below that, anything goes.
  const protectedHere = total >= 5 && isProtectedIndex(total, index);
  // Which anchor is this slide, if any? Drives the lock badge label.
  const anchorKind: AnchorType | null = protectedHere
    ? index === 0
      ? "TITLE"
      : index === total - 2
        ? "QUESTION"
        : index === total - 1
          ? "FINAL"
          : null
    : null;
  const anchorLabel =
    anchorKind === "TITLE"
      ? "Opening"
      : anchorKind === "QUESTION"
        ? "The Ask"
        : anchorKind === "FINAL"
          ? "Celebration"
          : null;
  // The type-select dropdown shows only the locked type when this row
  // is an anchor. Anchors can still have their CONTENT edited; just
  // not their slide kind.
  const lockedTypeOptions: string[] = anchorKind
    ? [anchorKind]
    : SLIDE_TYPES.map((t) => t.value);

  return (
    <div
      className={cn(
        "card overflow-hidden transition-all duration-300",
        expanded ? "border-rose/35 shadow-[0_8px_40px_rgba(244,88,126,.1)]" : "hover:border-soft",
        protectedHere && "ring-1 ring-amber-400/30",
      )}
    >
      {/* header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            disabled={index === 0 || protectedHere}
            onClick={() => onMove(-1)}
            className="cursor-pointer rounded p-0.5 text-muted transition-colors hover:bg-white/8 hover:text-cream disabled:cursor-not-allowed disabled:opacity-25"
            aria-label="Move slide up"
            title={
              protectedHere
                ? `${anchorLabel} position is locked`
                : "Move slide up"
            }
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            disabled={index === total - 1 || protectedHere}
            onClick={() => onMove(1)}
            className="cursor-pointer rounded p-0.5 text-muted transition-colors hover:bg-white/8 hover:text-cream disabled:cursor-not-allowed disabled:opacity-25"
            aria-label="Move slide down"
            title={
              protectedHere
                ? `${anchorLabel} position is locked`
                : "Move slide down"
            }
          >
            <ChevronDown size={14} />
          </button>
        </div>

        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-rose to-violet font-display text-[15px] font-bold text-white shadow-[0_4px_16px_rgba(244,88,126,.35)]">
          {index + 1}
        </div>

        <button type="button" onClick={onToggle} className="min-w-0 flex-1 cursor-pointer text-left">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[9.5px] font-bold uppercase tracking-[0.24em] text-dim">
              Slide {index + 1}
            </span>
            <Pill tone="cream">{meta.label}</Pill>
            {anchorLabel && (
              <Pill tone="gold" className="uppercase tracking-widest">
                ⛓ {anchorLabel}
              </Pill>
            )}
            {!slide.enableAutoAdvance && <Pill tone="gold">Manual</Pill>}
            <Pill tone="muted" >{slide.duration}s</Pill>
          </div>
          <p className="mt-1 truncate text-[12.5px] text-muted">{summary}</p>
        </button>

        <button
          type="button"
          onClick={onToggle}
          className="cursor-pointer rounded-lg border border-line p-1.5 text-muted transition-all duration-300 hover:border-soft hover:text-cream"
          aria-label={expanded ? "Collapse slide" : "Expand slide"}
        >
          <ChevronDown
            size={15}
            className={cn("transition-transform duration-300", expanded && "rotate-180")}
          />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={protectedHere}
          className="cursor-pointer rounded-lg border border-line p-1.5 text-muted transition-colors hover:border-rose/50 hover:bg-rose/10 hover:text-blush disabled:cursor-not-allowed disabled:opacity-25"
          aria-label="Delete slide"
          title={
            protectedHere
              ? `${anchorLabel} position is locked`
              : "Delete slide"
          }
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* body — animated grid-template-rows collapse */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="space-y-4 border-t border-line bg-abyss/35 px-4 py-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Slide type"
                hint={
                  anchorLabel
                    ? `${anchorLabel} position — type is locked`
                    : meta.hint
                }
              >
                <select
                  className={cn(
                    "inp cursor-pointer",
                    protectedHere && "cursor-not-allowed opacity-60",
                  )}
                  value={slide.type}
                  disabled={protectedHere}
                  onChange={(e) => onUpdate({ type: e.target.value as SlideType })}
                >
                  {lockedTypeOptions.map((t) => {
                    const opt = SLIDE_TYPES.find((s) => s.value === t);
                    return opt ? (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ) : null;
                  })}
                </select>
              </Field>
              <Field label="Duration (sec)" hint="1–120 seconds on screen">
                <input
                  type="number"
                  min={1}
                  max={120}
                  step={1}
                  className="inp"
                  value={slide.duration}
                  onChange={(e) => onUpdate({ duration: clampDuration(Number(e.target.value)) })}
                />
              </Field>
              <div className="flex flex-col gap-2 rounded-xl border border-line bg-panel/60 px-3.5 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[13px] font-semibold text-cream">Auto-advance</p>
                    <p className="mt-0.5 text-[11px] leading-snug text-dim">
                      Off → waits for a tap with a &ldquo;tap to continue&rdquo; hint
                    </p>
                  </div>
                  <Toggle checked={slide.enableAutoAdvance} onChange={(v) => onUpdate({ enableAutoAdvance: v })} />
                </div>
              </div>
            </div>

            <Field
              label={slide.type === "PHOTO" ? "Caption" : "Content text"}
              counter={`${slide.contentText.length} / 5000`}
            >
              <textarea
                rows={3}
                className="inp"
                placeholder={
                  slide.type === "QUESTION"
                    ? "Will you marry me?"
                    : slide.type === "PHOTO"
                      ? "Caption under the photo…"
                      : "Write in the proposer's voice…"
                }
                maxLength={5000}
                value={slide.contentText}
                onChange={(e) => onUpdate({ contentText: e.target.value.slice(0, 5000) })}
              />
            </Field>

            {(slide.type === "PHOTO" || slide.type === "MEMORY") && (
              <Field label={slide.type === "PHOTO" ? "Hero image" : "Memento image"}>
                <ImageOrDefaultUploader
                  value={slide.imageUrl}
                  onChange={(url) => onUpdate({ imageUrl: url })}
                  kinds={["bg", "fpv_flower", "fpv_ring", "fpv_dinner"]}
                />
              </Field>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Page Transition"
                hint={slide.pageTransitionId ? "Custom for this slide" : `Using deck default (${pageTransitionId ? 'see Details tab' : 'built-in cross-fade'})`}
              >
                <PageTransitionSelect
                  value={slide.pageTransitionId ?? pageTransitionId}
                  fallbackId={pageTransitionId}
                  onChange={(id) => onUpdate({ pageTransitionId: id })}
                />
              </Field>
              <Field
                label={
                  <span className="flex items-center justify-between gap-2">
                    Ambient effect
                    {tier === "FREE" && isProAmbient(slide.effectAmbient) && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-rose/80">PRO</span>
                    )}
                  </span>
                }
                hint={
                  tier === "FREE" && isProAmbient(slide.effectAmbient)
                    ? "This ambient requires a PRO template. Save will be rejected."
                    : recAm?.code === slide.effectAmbient
                      ? `★ Recommended for ${slide.type}`
                      : undefined
                }
              >
                <select
                  className="inp cursor-pointer"
                  value={slide.effectAmbient}
                  onChange={(e) => onUpdate({ effectAmbient: e.target.value })}
                >
                  <option value="none">None</option>
                  <optgroup label="Free effects">
                    {ambientOptionsFor(slideType).filter((o) => o.value !== 'none' && !o.label.includes("· PRO")).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Pro effects">
                    {ambientOptionsFor(slideType).filter((o) => o.label.includes("· PRO")).map((o) => (
                      <option key={o.value} value={o.value}>{o.label.replace(" · PRO", "")} · PRO</option>
                    ))}
                  </optgroup>
                </select>
              </Field>
            </div>

            <Field
              label={`Intensity · ${Math.round(slide.effectIntensity * 100)}%`}
              hint="Ambient layer density (fireflies, hearts, petals). 0 ≈ invisible, 1 ≈ saturated."
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={slide.effectIntensity}
                className="slider"
                onChange={(e) => onUpdate({ effectIntensity: Number(e.target.value) })}
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function TranslationsTab({
  translations,
  onUpdate,
  onRemove,
}: {
  translations: Translation[];
  onUpdate: (locale: string, p: Partial<Translation>) => void;
  onRemove: (locale: string) => void;
}) {
  const byLocale = new Map(translations.map((t) => [t.locale, t]));

  return (
    <div className="space-y-4">
      <div className="card px-4 py-3 text-[12.5px] leading-relaxed text-muted">
        <span className="font-semibold text-cream">Fallback:</span> locales without a row use the
        template's main name &amp; description. A row overrides only its own locale. Empty rows
        are dropped automatically on save.
      </div>

      <div className="space-y-2.5">
        {LOCALES.map((locale, i) => {
          const row = byLocale.get(locale.code);
          const active = !!row;
          return (
            <div
              key={locale.code}
              className={cn(
                "card overflow-hidden transition-all duration-300",
                active ? "border-mint/30" : "hover:border-soft",
              )}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              {!active ? (
                <button
                  type="button"
                  onClick={() => onUpdate(locale.code, { name: "" })}
                  className="flex w-full cursor-pointer items-center gap-2 px-4 py-3.5 text-left text-[13px] text-dim transition-colors hover:text-cream"
                >
                  <Plus size={14} className="text-mint/80" />
                  Add {locale.label} translation
                  <span className="ml-auto font-mono text-[10.5px] uppercase tracking-widest text-dim/60">
                    {locale.code}
                  </span>
                </button>
              ) : (
                <div className="px-4 py-3.5">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-bold text-cream">{locale.label}</span>
                      <span className="font-mono text-[10.5px] uppercase tracking-widest text-dim">{locale.code}</span>
                      <Pill tone="mint">Active</Pill>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemove(locale.code)}
                      className="cursor-pointer text-[11.5px] font-semibold text-blush/80 underline-offset-2 transition-colors hover:text-blush hover:underline"
                    >
                      Remove translation
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Localized name">
                      <input
                        className="inp"
                        placeholder="Template name in this locale"
                        value={row?.name ?? ""}
                        maxLength={120}
                        onChange={(e) => onUpdate(locale.code, { name: e.target.value.slice(0, 120) })}
                      />
                    </Field>
                    <Field label="Localized description">
                      <textarea
                        rows={2}
                        className="inp"
                        placeholder="Description in this locale"
                        value={row?.description ?? ""}
                        maxLength={240}
                        onChange={(e) => onUpdate(locale.code, { description: e.target.value.slice(0, 240) })}
                      />
                    </Field>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SettingsTab({
  form,
  onPatch,
}: {
  form: FormState;
  onPatch: (p: Partial<FormState>) => void;
}) {
  const isPro = form.tier === "PRO";

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-5">
        <div className="card divide-y divide-line/70 px-4 py-1">
          <div className="pt-2 pb-1">
            <p className="lbl">Visibility</p>
          </div>
          <ToggleRow
            title="Public listing"
            desc="Anyone can browse and use this template"
            checked={form.isPublic}
            onChange={(v) => onPatch({ isPublic: v })}
          />
          <ToggleRow
            title="Featured"
            desc="Showcase this template in the homepage carousel"
            checked={form.isFeatured}
            onChange={(v) => onPatch({ isFeatured: v })}
          />
        </div>

        <div className="card px-4 py-4">
          <p className="lbl mb-1">Proposal types</p>
          <p className="mb-3 text-xs text-dim">Same controls as the Details tab.</p>
          <ProposalPills
            value={form.proposalTypes}
            onChange={(v) => onPatch({ proposalTypes: v })}
            options={PROPOSAL_TYPES}
          />
        </div>
      </div>

      <div className="card px-4 py-4">
        <p className="lbl">Access tier</p>
        <p className="mb-3.5 mt-1 text-xs leading-relaxed text-dim">
          Controls who can use the template <em>and</em> which effects are allowed — FREE decks
          are limited to the {FREE_AMBIENT_COUNT} free ambient
          effects.
        </p>
        <div className="space-y-2">
          {TIERS.map((t) => {
            const selected = form.tier === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => onPatch({ tier: t.value })}
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-left transition-all duration-200",
                  selected
                    ? "border-rose/55 bg-rose/10 shadow-[0_0_24px_rgba(244,88,126,.18)]"
                    : "border-line bg-abyss/40 hover:border-soft",
                )}
              >
                <span>
                  <span className="block text-[13.5px] font-bold text-cream">{t.label}</span>
                  <span className="mt-0.5 block text-[11.5px] text-muted">{t.tagline}</span>
                </span>
                <span className="flex items-center gap-2">
                  <TierChip tier={t.value} />
                  <span
                    className={cn(
                      "flex h-4.5 w-4.5 items-center justify-center rounded-full border transition-all",
                      selected ? "border-rose bg-rose text-white" : "border-soft",
                    )}
                  >
                    {selected ? <span className="text-[10px]">✓</span> : null}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {!isPro ? (
          <div className="mt-4 rounded-lg border border-gold/30 bg-gold/8 px-3.5 py-2.5 text-[12px] leading-relaxed text-gold">
            FREE tier active — PRO ambient effects on any slide will be rejected at save time.
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-mint/25 bg-mint/6 px-3.5 py-2.5 text-[12px] leading-relaxed text-mint/90">
            All {AMBIENT_EFFECTS.length} ambient effects unlocked for
            this deck.
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── */
// PageTransitionSelect — dropdown picker for a single slide's
// transition. Admins can either inherit the deck-level default or
// pick a custom transition for that one slide. `null` = use default.
// ────────────────────────────────────────────────────────────────
function PageTransitionSelect({
  value,
  fallbackId,
  onChange,
}: {
  value: string | null;
  fallbackId: string | null;
  onChange: (id: string | null) => void;
}) {
  const { rows, loading } = usePageTransitions();

  if (loading) {
    return (
      <div className="inp flex items-center gap-2 text-dim">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
        Loading…
      </div>
    );
  }

  return (
    <select
      className="inp cursor-pointer"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    >
      <option value="">
        {fallbackId
          ? `— Default (deck level) —`
          : '— Default (built-in cross-fade) —'}
      </option>
      {rows.filter((r) => r.isEnabled).map((r) => (
        <option key={r.id} value={r.id}>
          {r.emoji ?? '•'} {r.name}{r.tier === 'PRO' ? ' · PRO' : ''}
        </option>
      ))}
    </select>
  );
}

/* ──────────────────────────────────────────────────────────────── */
// PageTransitionIndicator — small caption under the Entry effect
// select that surfaces the current page-transition choice so admins
// don't have to scroll back to the deck-level picker to see what's
// set. Pulls from the same shared catalogue hook.
// ────────────────────────────────────────────────────────────────

function PageTransitionIndicator({ pageTransitionId }: { pageTransitionId: string | null }) {
  const { rows } = usePageTransitions();
  if (!pageTransitionId) {
    return (
      <p className="mt-1.5 text-[11px] text-dim">
        Page transition: <span className="text-white/70">built-in cross-fade</span>
      </p>
    );
  }
  const row = rows.find((r) => r.id === pageTransitionId);
  if (!row) {
    return (
      <p className="mt-1.5 text-[11px] text-amber">
        Page transition: <code className="font-mono">{pageTransitionId}</code> (not found)
      </p>
    );
  }
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-dim">
      <span>Page transition:</span>
      <span className="text-white/85">{row.emoji ?? '•'} {row.name}</span>
      {row.tier === 'PRO' && <span className="rounded-full bg-rose/15 px-1.5 py-0.5 font-bold uppercase tracking-wider text-rose/90">PRO</span>}
    </p>
  );
}

/* ──────────────────────────────────────────────────────────────── */
// PageTransitionField — labelled <select> for the template-level
// page transition. Pulls the catalogue from `/admin/transitions`.
// Mirrors the picker that lives inside ProposalPreview's modal
// header so admins have a single mental model.
// ────────────────────────────────────────────────────────────────

function PageTransitionField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const { rows, loading, error } = usePageTransitions();

  // Don't render at all while the catalogue loads — keeps the
  // form layout stable and avoids a flash of "Default" content.
  if (loading) {
    return (
      <Field label="Page transition" hint="Loading transitions…">
        <div className="inp flex items-center gap-2 text-dim">
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Loading…
        </div>
      </Field>
    );
  }

  return (
    <Field
      label="Page transition"
      hint={
        error
          ? error
          : 'Plays between every slide. Affects the whole deck, not individual slides — preview it in the modal.'
      }
    >
      <select
        className="inp cursor-pointer"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">Default (built-in cross-fade)</option>
        {rows.filter((r) => r.isEnabled).map((r) => (
          <option key={r.id} value={r.id}>
            {r.emoji ?? '•'} {r.name}{r.tier === 'PRO' ? ' · PRO' : ''}
          </option>
        ))}
      </select>
    </Field>
  );
}